import { useMutation, useQuery } from '@apollo/client';
import { FC, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Spinner } from 'reactstrap';
import { AppContext } from '../../../context';
import { ALL_MODAL_ACCCESS_SETTINGS } from '../../moduleAccess-settings/gql';
import { checkUserFeatureAccess } from '../../../utils/commonFn';
import { REQUEST_AI_SCORE } from '../gql';
import AiScoreModal from './AiScoreModal';

const SCORE_COLOR = (score: number) =>
  score >= 7 ? '#28a745' : score >= 4 ? '#eaa827' : '#dc3545';

interface ScoreState {
  aiScore: number;
  aiScoreReason: string;
  aiScoredResumeId: string;
  aiScoredAt: string;
}

interface Props {
  jobApplicantId: string;
  candidateId: string;
  aiScore?: number;
  aiScoreReason?: string;
  aiScoredResumeId?: string;
  aiScoredAt?: string;
  onScoreUpdated?: (data: ScoreState) => void;
  /** True while a bulk "AI Fit Score" run is scoring this applicant (kan-116). */
  busy?: boolean;
}

const AiScoreBadge: FC<Props> = ({
  jobApplicantId,
  candidateId,
  aiScore: initialScore,
  aiScoreReason: initialReason,
  aiScoredResumeId: initialResumeId,
  aiScoredAt: initialScoredAt,
  onScoreUpdated,
  busy = false,
}) => {
  const { user } = useContext(AppContext);
  const { data: settingsData } = useQuery(ALL_MODAL_ACCCESS_SETTINGS);
  const [score, setScore] = useState<ScoreState | null>(
    initialScore != null
      ? { aiScore: initialScore, aiScoreReason: initialReason ?? '', aiScoredResumeId: initialResumeId ?? '', aiScoredAt: initialScoredAt ?? '' }
      : null,
  );
  const [modalOpen, setModalOpen] = useState(false);

  // kan-120: the seed above runs once per instance, so a reused instance (a
  // parent list keyed by index, e.g. after a row is declined) kept showing the
  // previous applicant's score. Re-sync whenever the identity or the
  // server-side score props change. Deps-only on purpose: a parent re-render
  // with UNCHANGED (still-null) props does not re-run this, so a score the
  // badge just fetched itself is never clobbered.
  useEffect(() => {
    setScore(
      initialScore != null
        ? { aiScore: initialScore, aiScoreReason: initialReason ?? '', aiScoredResumeId: initialResumeId ?? '', aiScoredAt: initialScoredAt ?? '' }
        : null,
    );
  }, [jobApplicantId, initialScore, initialReason, initialResumeId, initialScoredAt]);

  // kan-120: a modal opened for one applicant must not stay open for another.
  useEffect(() => {
    setModalOpen(false);
  }, [jobApplicantId]);

  const [requestScore, { loading }] = useMutation(REQUEST_AI_SCORE, {
    onCompleted: (data) => {
      const result = data?.requestAiScore;
      if (result) {
        const s: ScoreState = {
          aiScore: result.aiScore,
          aiScoreReason: result.aiScoreReason,
          aiScoredResumeId: result.aiScoredResumeId,
          aiScoredAt: result.aiScoredAt,
        };
        setScore(s);
        onScoreUpdated?.(s);
        toast.success('AI score generated');
      }
    },
    onError: (err) => toast.error(err.message),
  });

  if (!checkUserFeatureAccess(user?.id, 'AI_MATCH_SCORING', settingsData)) {
    return null;
  }

  const handleUnscored = () => {
    if (!window.confirm('Get AI recommendation score for this applicant?')) return;
    requestScore({ variables: { jobApplicantId } });
  };

  if (loading || busy) {
    return (
      <span
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 6, fontSize: 12, color: '#6c757d' }}
        title={busy ? 'Scoring in progress' : undefined}
      >
        <Spinner size="sm" />
        {busy && <span>Scoring…</span>}
      </span>
    );
  }

  if (!score) {
    return (
      <button
        onClick={handleUnscored}
        title="Get AI resume score"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 3,
          marginLeft: 6,
          padding: '1px 6px',
          fontSize: 12,
          fontWeight: 600,
          border: '1px solid #ced4da',
          borderRadius: 10,
          background: '#f8f9fa',
          color: '#6c757d',
          cursor: 'pointer',
          verticalAlign: 'middle',
          lineHeight: 1.4,
        }}
      >
        ✦ AI
      </button>
    );
  }

  const color = SCORE_COLOR(score.aiScore);

  return (
    <>
      <button
        onClick={() => setModalOpen(true)}
        title={`AI Score: ${score.aiScore}/10`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginLeft: 6,
          padding: '1px 7px',
          height: 22,
          borderRadius: 10,
          background: color,
          color: '#fff',
          border: 'none',
          fontSize: 12,
          fontWeight: 700,
          cursor: 'pointer',
          verticalAlign: 'middle',
          lineHeight: 1,
          flexShrink: 0,
          whiteSpace: 'nowrap',
        }}
      >
        {score.aiScore}/10
      </button>

      <AiScoreModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        jobApplicantId={jobApplicantId}
        candidateId={candidateId}
        aiScore={score.aiScore}
        aiScoreReason={score.aiScoreReason}
        aiScoredResumeId={score.aiScoredResumeId}
        onRescored={(data) => {
          const s: ScoreState = {
            aiScore: data.aiScore,
            aiScoreReason: data.aiScoreReason,
            aiScoredResumeId: data.aiScoredResumeId,
            aiScoredAt: data.aiScoredAt,
          };
          setScore(s);
          onScoreUpdated?.(s);
          setModalOpen(false);
        }}
      />
    </>
  );
};

export default AiScoreBadge;
